<div class="cm-landing-fixed-width">
	<form [formGroup]="form"
		*ngIf="form">
		<h2>CropManage Nitrogen Recommendation Calculator</h2>

		<h4>Crop Information</h4>
		<mat-form-field
			*ngIf="commodityTypes"
			appearance="fill"
			class="mat-form-field--one-line mat-form-field--short">

			<mat-label>{{ 'Commodity' | translate }}</mat-label>

			<mat-select required
				formControlName="CommodityTypeId">

				<mat-option *ngFor="let commodityType of commodityTypes"
					[value]="commodityType.Id">
					{{ commodityType.Name | translate }}
				</mat-option>

			</mat-select>

			<mat-error *ngIf="f.CommodityTypeId.invalid && (f.CommodityTypeId.dirty || f.CommodityTypeId.touched)">
				{{ 'Cannot be empty. Please select an option.' | translate }}
			</mat-error>

		</mat-form-field>

		<mat-form-field
			*ngIf="cropTypes && cropTypes.length > 0"
			appearance="fill"
			class="mat-form-field--one-line mat-form-field--short">

			<mat-label>{{ 'CropType' | translate }}</mat-label>

			<mat-select required
				formControlName="CropTypeId">

				<mat-option *ngFor="let cropType of cropTypes"
					[value]="cropType.Id">
					{{ cropType.Name }}
				</mat-option>

			</mat-select>

			<mat-error *ngIf="f.CropTypeId.invalid && (f.CropTypeId.dirty || f.CropTypeId.touched)">
				{{ 'Cannot be empty. Please select an option.' | translate }}
			</mat-error>

		</mat-form-field>

		<ng-container *ngIf="isTree">
			<mat-form-field appearance="fill"
				class="mat-form-field--short">
				<mat-label>Orchard age</mat-label>
				<mat-select required
					formControlName="AgeOfCrop">
					<mat-option *ngFor="let age of ageOptions"
						[value]="age.key">
						{{ age.value }}
					</mat-option>
				</mat-select>
				<mat-error *ngIf="f.AgeOfCrop.invalid && (f.AgeOfCrop.dirty || f.AgeOfCrop.touched)">
					{{ 'Cannot be empty. Please select an option.' | translate }}
				</mat-error>
			</mat-form-field>

			<mat-form-field appearance="fill"
				class="mat-form-field--short">
				<mat-label>Vigor</mat-label>
				<mat-select formControlName="IsHighVigor">
					<mat-option *ngFor="let vigorOption of vigorOptions"
						[value]="vigorOption.key">
						{{ vigorOption.value }}
					</mat-option>
				</mat-select>
			</mat-form-field>

			<div class="n-calculator__tree">
				<div class="n-calculator__tree-col">
					<mat-form-field appearance="fill"
						class="mat-form-field--short">
						<mat-label>Estimated Yield</mat-label>
						<input matInput
							type="number"
							step="0.1"
							formControlName="YieldTarget">
						<span matSuffix class="mat-form-field-suffix--padded">
							<span *ngIf="yieldUnitId === YIELD_UNIT_IDS.LBS">lbs/acre</span>
							<span *ngIf="yieldUnitId === YIELD_UNIT_IDS.TONS">tons/acre</span>
						</span>
						<mat-error *ngIf="f.YieldTarget.invalid && (f.YieldTarget.dirty || f.YieldTarget.touched)">
							Enter a yield greater than zero.
						</mat-error>
					</mat-form-field>

					<h4>Nitrogen Credits</h4>
					<mat-form-field appearance="fill"
						class="mat-form-field--short">
						<mat-label>Manure N Credits</mat-label>
						<input matInput type="number" min="0" formControlName="ManureN">
						<span matSuffix class="mat-form-field-suffix--padded">
							<i class="bk-calculation-icon fa fa-calculator"
								(click)="openManureCalculator($event)"
								matTooltip="Open calculator">
							</i>
							lbs N/acre
						</span>
					</mat-form-field>
					<mat-form-field appearance="fill"
						class="mat-form-field--short">
						<mat-label>Compost N Credits</mat-label>
						<input matInput type="number" min="0" formControlName="CompostN">
						<span matSuffix class="mat-form-field-suffix--padded">
							<i class="bk-calculation-icon fa fa-calculator"
								(click)="openCompostCalculator($event)"
								matTooltip="Open calculator">
							</i>
							lbs N/acre
						</span>
					</mat-form-field>
					<mat-form-field appearance="fill"
						class="mat-form-field--short">
						<mat-label>Cover Crop N Credits</mat-label>
						<input matInput type="number" min="0" formControlName="CoverCropN">
						<span matSuffix class="mat-form-field-suffix--padded">
							<i class="bk-calculation-icon fa fa-calculator"
								(click)="openCoverCropCalculator($event)"
								matTooltip="Open calculator">
							</i>
							lbs N/acre
						</span>
					</mat-form-field>
				</div>

				<div class="n-calculator__tree-col n-calculator__tree-col--leaves"
					*ngIf="showSpringLeaves">
					<h4>Spring Leaf Nutrient Values (optional)</h4>
					<mat-form-field *ngFor="let leaf of springLeafFields"
						appearance="fill"
						class="mat-form-field--short">
						<mat-label>{{ leaf.label }}</mat-label>
						<input matInput
							type="number"
							step="0.01"
							[formControlName]="leaf.name">
						<span matSuffix class="mat-form-field-suffix--padded">{{ leaf.unit }}</span>
					</mat-form-field>
				</div>
			</div>
		</ng-container>

		<h4>Crop Season Dates</h4>

		<div>
			<mat-form-field appearance="fill"
				class="bk-mat-date-field bk-mat-date-field--first">
				<mat-label>{{ 'Start Date' | translate }}</mat-label>
				<input matInput [matDatepicker]="wetDatePicker"
					formControlName="WetDate"
					required
					[min]="wetDateRange.minDate"
					[max]="wetDateRange.maxDate">
				<mat-datepicker-toggle matSuffix [for]="wetDatePicker"></mat-datepicker-toggle>
				<mat-datepicker #wetDatePicker></mat-datepicker>
				<mat-error *ngIf="f.WetDate.invalid && (f.WetDate.dirty || f.WetDate.touched)">
					{{ 'Planting Event Date Error Text' | translate }}
				</mat-error>
			</mat-form-field>

			<mat-form-field appearance="fill"
				class="bk-mat-date-field">
				<mat-label>{{ 'End Date' | translate }}</mat-label>
				<input matInput [matDatepicker]="harvestDatePicker"
					formControlName="HarvestDate"
					required
					[min]="harvestDateRange.minDate"
					[max]="harvestDateRange.maxDate">
				<mat-datepicker-toggle matSuffix [for]="harvestDatePicker"></mat-datepicker-toggle>
				<mat-datepicker #harvestDatePicker></mat-datepicker>
				<mat-error *ngIf="f.HarvestDate.invalid && (f.HarvestDate.dirty || f.HarvestDate.touched)">
					{{ 'Planting Event Date Error Text' | translate }}
				</mat-error>
			</mat-form-field>
		</div>

		<h4>Fertilization</h4>

		<mat-form-field appearance="fill"
			class="bk-mat-date-field bk-mat-date-field--first">
			<mat-label>{{ 'Event Date' | translate }}</mat-label>
			<input matInput [matDatepicker]="eventDatePicker"
				formControlName="EventDate"
				required
				[min]="eventDateRange.minDate"
				[max]="eventDateRange.maxDate">
			<mat-datepicker-toggle matSuffix [for]="eventDatePicker"></mat-datepicker-toggle>
			<mat-datepicker #eventDatePicker></mat-datepicker>
			<mat-hint align="start">
				Date of this nitrogen application. It must fall within the season dates.
			</mat-hint>
			<mat-error *ngIf="f.EventDate.invalid && (f.EventDate.dirty || f.EventDate.touched)">
				{{ 'Event Date Error Text' | translate }}
			</mat-error>
		</mat-form-field>

		<mat-form-field *ngIf="f.CommodityTypeId.value && !isFertilizationsPerMonth"
			appearance="fill"
			class="bk-mat-date-field">
			<mat-label>{{ 'Days to Next Fertilization' | translate }}</mat-label>
			<input matInput
				type="number"
				required
				formControlName="DaysToNextFertilization">
			<mat-hint align="start">
				Nitrogen is recommended for the period until the next application.
			</mat-hint>
			<mat-error *ngIf="f.DaysToNextFertilization.invalid && (f.DaysToNextFertilization.dirty || f.DaysToNextFertilization.touched)">
				{{ 'Enter a whole number of at least 1.' | translate }}
			</mat-error>
		</mat-form-field>

		<mat-form-field *ngIf="f.CommodityTypeId.value && isFertilizationsPerMonth"
			appearance="fill"
			class="bk-mat-date-field">
			<mat-label>{{ 'Fertilizations per Month' | translate }}</mat-label>
			<input matInput
				type="number"
				required
				formControlName="FertilizationsPerMonth">
			<mat-hint align="start">
				Strawberry and caneberry use applications per month, from 1 to 10.
			</mat-hint>
			<mat-error *ngIf="f.FertilizationsPerMonth.invalid && (f.FertilizationsPerMonth.dirty || f.FertilizationsPerMonth.touched)">
				{{ 'Enter a whole number from 1 to 10.' | translate }}
			</mat-error>
		</mat-form-field>

		<h4>Location</h4>
		<p>
			Click your ranch location on the map.
		</p>
		<agm-map
			(mapClick)="clickMap($event)"
			[latitude]="36.7"
			[longitude]="-119.8"
			[zoom]="6"
			[mapTypeId]="'hybrid'">
			<agm-marker *ngIf="f.Latitude.value"
				[iconUrl]="'../assets/images/icons/map_marker-planting.png'"
				[latitude]="f.Latitude.value"
				[longitude]="f.Longitude.value">
			</agm-marker>
		</agm-map>

		<h4>Soil Nitrate</h4>

		<mat-form-field appearance="fill"
			class="bk-mat-date-field bk-mat-date-field--first">
			<mat-label>{{ 'Nitrate' | translate }}</mat-label>
			<input matInput
				type="number"
				step="0.1"
				formControlName="SoilN">
			<span matSuffix class="mat-form-field-suffix--padded">
				ppm NO3-N
			</span>
		</mat-form-field>

		<mat-form-field appearance="fill"
			class="bk-mat-date-field">
			<mat-label>{{ 'Depth' | translate }}</mat-label>
			<mat-select formControlName="SampleDepth">
				<mat-option *ngFor="let depth of sampleDepths"
					[value]="depth.value">
					{{ depth.label }}
				</mat-option>
			</mat-select>
		</mat-form-field>

		<ng-container *ngIf="detail">
			<div *ngIf="detail.SeasonalN < 0"
				class="bk-modal__warning-section">
				Nitrogen credits exceed this crop's nitrogen requirement.
			</div>
			<div class="n-calculator__summary-row">
				<div class="n-calculator__summary-col">
					<table class="n-calculator__summary-table" cellpadding="10">
						<tbody>
							<tr>
								<td>Recommended nitrogen</td>
								<td>{{ detail.Recommendation | number:'1.2-2' }} lbs N/acre</td>
							</tr>
							<tr *ngIf="detail.SeasonalN != null">
								<td>Seasonal N requirement</td>
								<td>{{ detail.SeasonalN | number:'1.2-2' }} lbs N/acre</td>
							</tr>
							<tr>
								<td>
									Uptake during this interval
									<span class="n-calculator__hint">
										{{ detail.DaysToNextFertilization }} days
									</span>
								</td>
								<td>{{ detail.NUptakeDifference | number:'1.2-2' }} lbs N/acre</td>
							</tr>
							<tr *ngIf="!detail.NearHarvest && detail.IncludeSoilN">
								<td>Soil N threshold − soil N</td>
								<td>{{ (detail.SoilNThreshold - detail.SoilN) | number:'1.2-2' }} lbs N/acre</td>
							</tr>
							<tr *ngIf="!detail.NearHarvest">
								<td>Mineralized N</td>
								<td>{{ detail.NMineralTotal | number:'1.2-2' }} lbs N/acre</td>
							</tr>
							<tr *ngIf="detail.FertilizationsPerMonth">
								<td>Fertilizations per month</td>
								<td>{{ detail.FertilizationsPerMonth | number:'1.0-0' }}</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="n-calculator__summary-col n-calculator__summary-col--chart"
					*ngIf="detail.Uptake && detail.Uptake.length">
					<button type="button"
						class="n-calculator__zoom"
						(click)="zoomUptake(!uptakeZoomedOut)">
						{{ uptakeZoomedOut ? 'Interval' : 'Zoom out' }}
					</button>
					<div class="bk-chart n-calculator__uptake-chart" #uptakeChart></div>
				</div>
			</div>

			<button mat-button
				*ngIf="formChanged"
				type="button"
				[disabled]="!form.valid || isSaving"
				class="bk-button bk-button--medium n-calculator__calculate"
				(click)="calculate()">
				Recalculate
			</button>

			<div class="registration-prompt">
				<a routerLink="/register" class="registration-prompt__cta">Create a free account &rarr;</a>
			</div>
		</ng-container>

		<mat-form-field class="hp"
			appearance="fill">
			<mat-label>{{ 'Company' | translate }}</mat-label>
			<input matInput
				type="text"
				tabindex="-1"
				autocomplete="off"
				aria-hidden="true"
				formControlName="Company">
		</mat-form-field>

		<button mat-button
			*ngIf="!detail"
			type="button"
			[disabled]="!form.valid || isSaving"
			class="bk-button bk-button--medium n-calculator__calculate"
			(click)="calculate()">

			{{ (isSaving ? 'Calculating' : 'Calculate') | translate }}
			<img class="bk-button__loader" *ngIf="isSaving" src="/assets/images/loader-20px.gif"/>
		</button>
		<br>
		<br>
	</form>
</div>
